import type { ComponentProps, PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";
import { ChannelProvider, useAbly } from "ably/react";
import { getError } from "@remi/error";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";

import { isSuppressibleAblyError } from "~/utils/ablyErrors";

type DetachableChannelProviderProps = PropsWithChildren<{
  channelName: string;
  // Must be a module constant: `ChannelProvider` re-runs `setOptions` on this identity, and changed
  // params re-attach a channel that is already being listened to.
  options?: ComponentProps<typeof ChannelProvider>["options"];
}>;

const ACTIVE_CHANNEL_PROVIDER_COUNT = new Map<string, number>();

export const activeChannelProviderCount = (channelName: string): number =>
  ACTIVE_CHANNEL_PROVIDER_COUNT.get(channelName) ?? 0;

// Ably's `ChannelProvider` throws during render on a duplicate channel name, so this has to be readable
// during a descendant's render — an effect runs child-first and settles too late to prevent the throw.
const ProvidedChannelNamesContext = createContext<ReadonlySet<string>>(new Set<string>());

const isReleaseableChannelState = (state: string) => {
  return state === "initialized" || state === "detached" || state === "failed";
};

export const DetachableChannelProvider = ({ children, channelName, options }: DetachableChannelProviderProps) => {
  const providedByAncestors = useContext(ProvidedChannelNamesContext);

  if (providedByAncestors.has(channelName)) {
    return <>{children}</>;
  }

  return (
    <OwnedChannelProvider channelName={channelName} options={options} providedByAncestors={providedByAncestors}>
      {children}
    </OwnedChannelProvider>
  );
};

const OwnedChannelProvider = ({
  children,
  channelName,
  options,
  providedByAncestors,
}: DetachableChannelProviderProps & { providedByAncestors: ReadonlySet<string> }) => {
  const ably = useAbly();
  const providedNames = useMemo(
    () => new Set<string>([...providedByAncestors, channelName]),
    [providedByAncestors, channelName],
  );

  useEffect(() => {
    ACTIVE_CHANNEL_PROVIDER_COUNT.set(channelName, (ACTIVE_CHANNEL_PROVIDER_COUNT.get(channelName) ?? 0) + 1);

    return () => {
      const nextCount = Math.max((ACTIVE_CHANNEL_PROVIDER_COUNT.get(channelName) ?? 1) - 1, 0);
      if (nextCount > 0) {
        ACTIVE_CHANNEL_PROVIDER_COUNT.set(channelName, nextCount);
        return;
      }

      ACTIVE_CHANNEL_PROVIDER_COUNT.delete(channelName);
      const detachAndReleaseChannel = async () => {
        const channel = ably.channels.get(channelName);
        if (!isReleaseableChannelState(channel.state)) {
          try {
            await channel.detach();
          } catch (error: unknown) {
            if (isSuppressibleAblyError(error)) {
              remiLogger.captureCounterMetric("ably.channel.operation-suppressed", 1, {
                attributes: { channelType: channelName.split(":")[0], operation: "detach" },
              });
              return;
            } else {
              remiLogger.captureError(getError(error));
            }
          }
        }
        if (isReleaseableChannelState(channel.state)) {
          ably.channels.release(channelName);
        }
      };

      void detachAndReleaseChannel();
    };
  }, [ably, channelName]);

  return (
    <ProvidedChannelNamesContext.Provider value={providedNames}>
      <ChannelProvider channelName={channelName} options={options}>
        {children}
      </ChannelProvider>
    </ProvidedChannelNamesContext.Provider>
  );
};
